import CoverageTimeSelect from '@/dashboard/coc/CoverageTimeSelect';
import DoctorCoverResults from './DoctorCoverResults';
import { getSupabaseBrowserClient } from '@/lib/supabase';
import { formatCoverageDate } from '@/dashboard/coc/doctor-coverage-view';
import { useEffect, useRef, useState } from 'react';
import { portalApiGet, portalApiPost, PortalApiError } from '@/lib/portal-api';
import { AcInput, AcSelect, FormField } from '@/dashboard/ui/FormControl';
import AcButton from '@/dashboard/ui/AcButton';
import ModalShell from '@/dashboard/ui/ModalShell';
import './reception-cover-report.css';
import type { ClinicRosterDoctor } from '@/dashboard/types';
interface Report {
  report_id: string;
  doctor_id: number;
  affected_date: string;
  detail: string;
  verification: string;
  recorded_at: string;
  affected_bookings: unknown[];
}
interface Booking {
  appointmentId: number;
  patientId: string | null;
  patientName: string | null;
  eligible: boolean;
  refusal: string | null;
  expected: { doctorId: number | null; appointmentTime: string; status: string; updatedAt: string };
}
interface Coordination {
  id: number;
  version: number;
  legacy_doctor_id: number;
  status: string;
  starts_at: string;
  ends_at: string;
}
interface Affected {
  report: Report;
  bookings: Booking[];
  coordination: Coordination[];
}
export default function ReceptionCoverPanel({
  clinicId,
  clinicName,
  date,
  doctors,
  mode,
  onClose,
  onChanged,
}: {
  clinicId: number;
  clinicName: string;
  date: string;
  doctors: readonly ClinicRosterDoctor[];
  mode: 'report' | 'resolve';
  onClose: () => void;
  onChanged: () => void;
}) {
  const [issue, setIssue] = useState('Doctor not here'),
    [unknown, setUnknown] = useState(true);
  const [day, setDay] = useState(date),
    [doctor, setDoctor] = useState(''),
    [change, setChange] = useState('absence'),
    [detail, setDetail] = useState('');
  const [reports, setReports] = useState<Report[] | null>(null),
    [affected, setAffected] = useState<Affected | null>(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [message, setMessage] = useState('');
  const pending = useRef<Record<string, unknown> | null>(null),
    epoch = useRef(0),
    saving = useRef(false);
  const [from, setFrom] = useState(''),
    [until, setUntil] = useState(''),
    [draftReady, setDraftReady] = useState(false);
  const draftKey = useRef<string | null>(null);
  const guarded = busy;
  useEffect(() => {
    let active = true;
    setDraftReady(false);
    draftKey.current = null;
    pending.current = null;
    setDay(date);
    setDoctor('');
    setChange('absence');
    setDetail('');
    setFrom('');
    setUntil('');
    setIssue('Doctor not here');
    setUnknown(true);
    void (async () => {
      const client = getSupabaseBrowserClient();
      const actor = client ? (await client.auth.getSession()).data.session?.user.id : null;
      if (!actor) throw new Error('Session unavailable.');
      const key = `cover-report:v1:${actor}:${clinicId}`;
      const raw = sessionStorage.getItem(key);
      const saved = raw ? JSON.parse(raw) : null;
      if (!active) return;
      if (saved?.pending && saved.pending.clinicId !== clinicId)
        throw new Error('Saved report scope changed');
      draftKey.current = key;
      if (saved?.draft) {
        setDay(saved.draft.day);
        setDoctor(saved.draft.doctor);
        setChange(saved.draft.change);
        setDetail(saved.draft.detail);
        setFrom(saved.draft.from ?? '');
        setUntil(saved.draft.until ?? '');
        setIssue(
          saved.draft.issue ??
            (saved.draft.change === 'slot' ? 'Late / shift change reported' : 'Doctor not here'),
        );
        setUnknown(saved.draft.unknown ?? (!saved.draft.from && !saved.draft.until));
        pending.current = saved.pending ?? null;
      }
      setDraftReady(true);
    })().catch(() => {
      if (active)
        setError('The saved report could not be restored. Reopen after checking the session.');
    });
    return () => {
      active = false;
    };
  }, [clinicId]);
  useEffect(() => {
    if (!draftReady || !draftKey.current) return;
    try {
      sessionStorage.setItem(
        draftKey.current,
        JSON.stringify({
          draft: { day, doctor, change, detail, from, until, issue, unknown },
          pending: pending.current,
        }),
      );
    } catch {
      setDraftReady(false);
      setError('This browser could not keep the report. Save is unavailable.');
    }
  }, [draftReady, day, doctor, change, detail, from, until, issue, unknown, busy]);
  async function load() {
    const token = ++epoch.current;
    setReports(null);
    try {
      const result = await portalApiGet<{ reports: Report[] }>(
        `/reception/doctor-absence-reports?clinicId=${clinicId}&date=${encodeURIComponent(day)}`,
      );
      if (token === epoch.current) setReports(result.reports);
    } catch {
      if (token === epoch.current) setError('Reports could not be loaded. Retry.');
    }
  }
  useEffect(() => {
    setAffected(null);
    setError('');
    void load();
    return () => {
      epoch.current++;
    };
  }, [clinicId, day]);
  async function open(id: string) {
    const token = ++epoch.current;
    setAffected(null);
    setError('');
    try {
      const result = await portalApiGet<Affected>(
        `/reception/doctor-absence-reports/${id}/affected-bookings?clinicId=${clinicId}`,
      );
      if (token === epoch.current) setAffected(result);
    } catch {
      if (token === epoch.current) setError('Affected bookings could not be loaded. Retry.');
    }
  }
  async function save() {
    if (saving.current || !draftReady || !draftKey.current) return;
    saving.current = true;
    const token = epoch.current;
    setBusy(true);
    setError('');
    try {
      pending.current ??= {
        clinicId,
        requestId: crypto.randomUUID(),
        doctorId: Number(doctor),
        date: day,
        change,
        detail: issue + (detail.trim() ? ' · ' + detail.trim() : ''),
        verification: 'unconfirmed',
        startsAt: !unknown && from ? new Date(day + 'T' + from + ':00+05:30').toISOString() : null,
        endsAt: !unknown && until ? new Date(day + 'T' + until + ':00+05:30').toISOString() : null,
      };
      sessionStorage.setItem(
        draftKey.current,
        JSON.stringify({
          draft: { day, doctor, change, detail, from, until, issue, unknown },
          pending: pending.current,
        }),
      );
      const result = await portalApiPost<{ report: Report }>(
        '/reception/doctor-absence-reports',
        pending.current,
      );
      if (token !== epoch.current) return;
      pending.current = null;
      sessionStorage.removeItem(draftKey.current);
      setDetail('');
      setDoctor('');
      setFrom('');
      setUntil('');
      setMessage('Report saved. Appointments unchanged.');
      setBusy(false);
      onChanged();
      await load();
      await open(result.report.report_id);
    } catch (e) {
      if (token !== epoch.current) return;
      if (e instanceof PortalApiError && [400, 401, 403, 409].includes(e.status))
        pending.current = null;
      setError('Save not confirmed. Your entries remain; retry.');
    } finally {
      saving.current = false;
      if (token === epoch.current) setBusy(false);
    }
  }
  const existing = reports?.find((r) => r.doctor_id === Number(doctor) && r.affected_date === day);
  const canSave =
    Boolean(pending.current) ||
    Boolean(existing) ||
    (doctor !== '' && issue !== '' && (unknown || (from !== '' && until !== '' && from < until)));
  if (affected)
    return (
      <DoctorCoverResults
        clinicId={clinicId}
        clinicName={clinicName}
        reportId={affected.report.report_id}
        role="receptionist"
        doctors={doctors}
        onClose={() => setAffected(null)}
        onChanged={onChanged}
      />
    );
  return (
    <ModalShell
      title={mode === 'report' ? 'Doctor not here' : 'Review cover results'}
      eyebrow={`${clinicName} · Reception`}
      onClose={onClose}
      closeDisabled={guarded}
      trapFocus
      testId="reception-cover-panel"
      footer={
        mode === 'report' ? (
          <AcButton
            variant="primary"
            form="reception-cover-report"
            type="submit"
            disabled={busy || !draftReady || !canSave}
          >
            {busy
              ? 'Saving…'
              : pending.current
                ? 'Retry save'
                : existing
                  ? 'View existing update'
                  : 'Report to COC'}
          </AcButton>
        ) : undefined
      }
    >
      {error && <p role="alert">{error}</p>}
      {message && <p role="status">{message}</p>}
      {mode === 'report' && (
        <form
          id="reception-cover-report"
          className="reception-cover-report"
          onSubmit={(e) => {
            e.preventDefault();
            if (existing && !pending.current) {
              void open(existing.report_id);
            } else {
              void save();
            }
          }}
        >
          <fieldset disabled={!draftReady || busy || pending.current !== null}>
            <div className="reception-cover-report__two">
              <FormField id="absence-doctor" label="Doctor">
                <AcSelect
                  id="absence-doctor"
                  required
                  value={doctor}
                  onChange={(e) => setDoctor(e.target.value)}
                >
                  <option value="">Choose doctor</option>
                  {doctors.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </AcSelect>
              </FormField>
              <FormField id="absence-day" label="Date">
                <AcInput
                  id="absence-day"
                  type="date"
                  required
                  value={day}
                  onChange={(e) => setDay(e.target.value)}
                />
              </FormField>
            </div>
            <FormField id="absence-change" label="What happened?">
              <AcSelect
                id="absence-change"
                required
                value={issue}
                onChange={(e) => {
                  setIssue(e.target.value);
                  setChange(e.target.value === 'Late / shift change reported' ? 'slot' : 'absence');
                }}
              >
                <option value="">Choose</option>
                <option>Doctor not here</option>
                <option>Leave reported</option>
                <option>Late / shift change reported</option>
              </AcSelect>
            </FormField>
            <details className="reception-cover-report__details">
              <summary>Add hours or a note</summary>
              <label className="reception-cover-report__unknown">
                <input
                  type="checkbox"
                  checked={unknown}
                  onChange={(e) => setUnknown(e.target.checked)}
                />
                Hours unknown · COC will check
              </label>
              <div className="reception-cover-report__two">
                <FormField id="absence-from" label="From · IST">
                  <CoverageTimeSelect
                    id="absence-from"
                    disabled={unknown}
                    value={from}
                    onChange={setFrom}
                  />
                </FormField>
                <FormField id="absence-until" label="Until · IST">
                  <CoverageTimeSelect
                    id="absence-until"
                    disabled={unknown}
                    value={until}
                    onChange={setUntil}
                  />
                </FormField>
              </div>
              <FormField id="absence-detail" label="Note (optional)">
                <textarea
                  className="ac-input"
                  rows={2}
                  id="absence-detail"
                  maxLength={160 - issue.length - 3}
                  value={detail}
                  onChange={(e) => setDetail(e.target.value)}
                  placeholder="What did the doctor say?"
                />
              </FormField>
            </details>
          </fieldset>
          <div className="reception-cover-report__notice">
            <strong>
              {existing ? 'COC already has this report.' : 'COC will check and arrange cover.'}
            </strong>
            <p>
              {existing
                ? 'No second report is needed.'
                : 'This is a report, not confirmed attendance.'}
            </p>
          </div>
        </form>
      )}
      <details className="reception-cover-report__saved">
        <summary>Saved reports</summary>
        {reports === null ? (
          <p role="status">Loading reports…</p>
        ) : reports.length === 0 ? (
          <p>No saved reports.</p>
        ) : (
          reports.map((r) => (
            <section key={r.report_id}>
              <h4>
                {doctors.find((d) => d.id === r.doctor_id)?.name ?? 'Doctor name unavailable'} ·{' '}
                {formatCoverageDate(r.affected_date)}
              </h4>
              <p>{r.detail}</p>
              <p>
                {r.affected_bookings.length} affected booking
                {r.affected_bookings.length === 1 ? '' : 's'}
              </p>
              <AcButton variant="ghost" disabled={busy} onClick={() => void open(r.report_id)}>
                Open affected bookings
              </AcButton>
            </section>
          ))
        )}
        {!busy && error && (
          <AcButton variant="ghost" onClick={() => void load()}>
            Reload reports
          </AcButton>
        )}
      </details>
    </ModalShell>
  );
}
